// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@utils/cn';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium ring-offset-background',
    'transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
    'shadow-[0_1px_0_rgba(255,255,255,0.35)_inset,0_12px_28px_rgba(68,100,136,0.08)]',
  ].join(' '),
  {
    variants: {
      variant: {
        default: [
          'glass-pill border-white/20 bg-[linear-gradient(180deg,rgba(75,140,255,0.98),rgba(75,140,255,0.86))] text-primary-foreground',
          'hover:-translate-y-px hover:brightness-105 hover:shadow-[0_1px_0_rgba(255,255,255,0.24)_inset,0_18px_36px_rgba(75,140,255,0.22)]',
          'dark:border-[rgba(112,175,255,0.18)] dark:shadow-[0_1px_0_rgba(255,255,255,0.08)_inset,0_18px_36px_rgba(2,8,20,0.34)]',
        ].join(' '),
        destructive: [
          'glass-pill border-white/10 bg-gradient-to-b from-rose-500/90 via-rose-600/90 to-rose-700/85 text-white',
          'hover:-translate-y-0.5 hover:brightness-105 hover:shadow-[0_1px_0_rgba(255,255,255,0.25)_inset,0_16px_36px_rgba(127,29,29,0.22)]',
          'dark:border-rose-400/20 dark:shadow-[0_1px_0_rgba(255,255,255,0.08)_inset,0_16px_36px_rgba(69,10,10,0.36)]',
        ].join(' '),
        outline: [
          'glass-pill text-foreground',
          'hover:-translate-y-px hover:border-white/30 hover:bg-white/80 dark:hover:bg-slate-950/45',
        ].join(' '),
        secondary: [
          'glass-pill bg-[rgba(75,140,255,0.08)] text-foreground',
          'hover:-translate-y-px hover:bg-[rgba(75,140,255,0.14)] dark:hover:bg-[rgba(112,175,255,0.16)]',
        ].join(' '),
        ghost: [
          'border border-transparent bg-transparent text-foreground/85 shadow-none',
          'hover:bg-[rgba(255,255,255,0.52)] hover:text-foreground dark:hover:bg-[rgba(255,255,255,0.08)]',
        ].join(' '),
        link: 'text-primary underline-offset-4 hover:underline text-shadow-sm',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 px-3',
        lg: 'h-11 px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, isLoading, asChild, children, disabled, ...props }, ref) => {
    const mergedClassName = cn(buttonVariants({ variant, size, className }));
    const isDisabled = disabled || isLoading;
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={mergedClassName}
        ref={ref}
        disabled={asChild ? undefined : isDisabled}
        aria-disabled={asChild && isDisabled ? true : undefined}
        {...props}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          if (isDisabled) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          props.onClick?.(event);
        }}
      >
        {isLoading && (
          <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        )}
        <Slottable>{children}</Slottable>
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button };
